import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import TemplateEditor from './TemplateEditor';

export default function QueuesManager({ userId, onBack, lang = 'en' }) {
  const t = lang === 'fr' ? TR.fr : TR.en;
  const [templates, setTemplates] = useState([]);
  const [entryCounts, setEntryCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('user_queue_templates')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });
    if (err) { setError(err.message); setLoading(false); return; }
    setTemplates(data || []);

    const ids = (data || []).map((x) => x.id);
    if (ids.length) {
      const { data: entries } = await supabase
        .from('user_queue_entries').select('template_id').in('template_id', ids);
      const counts = {};
      (entries || []).forEach((e) => { counts[e.template_id] = (counts[e.template_id] || 0) + 1; });
      setEntryCounts(counts);
    } else {
      setEntryCounts({});
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleCreate = async () => {
    const name = window.prompt(t.promptName, '');
    if (!name || !name.trim()) return;
    setError(''); setBusy(true);
    const { data, error: rpcErr } = await supabase.rpc('create_queue_template', {
      p_user_id: userId, p_name: name.trim(),
    });
    setBusy(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    if (!data?.success) { setError(data?.error || t.errCreate); return; }
    fetchTemplates();
    if (data.template_id) setSelectedId(data.template_id);
  };

  const handleRename = async (template) => {
    const name = window.prompt(t.promptRename, template.name);
    if (!name || !name.trim() || name.trim() === template.name) return;
    setError(''); setBusy(true);
    const { data, error: rpcErr } = await supabase.rpc('rename_queue_template', {
      p_user_id: userId, p_template_id: template.id, p_name: name.trim(),
    });
    setBusy(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    if (!data?.success) { setError(data?.error || t.errRename); return; }
    fetchTemplates();
  };

  const handleDelete = async (template) => {
    if (!window.confirm(t.confirmDelete(template.name))) return;
    setError(''); setBusy(true);
    const { data, error: rpcErr } = await supabase.rpc('delete_queue_template', {
      p_user_id: userId, p_template_id: template.id,
    });
    setBusy(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    if (!data?.success) { setError(data?.error || t.errDelete); return; }
    fetchTemplates();
  };

  if (selectedId) {
    return (
      <TemplateEditor
        templateId={selectedId}
        userId={userId}
        lang={lang}
        onBack={() => { setSelectedId(null); fetchTemplates(); }}
      />
    );
  }

  return (
    <div className="pools-container">
      <div className="pools-header">
        <button className="pool-lobby-back" onClick={onBack}>← {t.back}</button>
        <h2>{t.title}</h2>
        <button className="pools-create-btn" onClick={handleCreate} disabled={busy}>+ {t.newTemplate}</button>
      </div>

      <p style={{ color: '#666', fontSize: 13 }}>{t.description}</p>

      {error && <div className="pool-form-error">{error}</div>}

      {loading && <div className="pools-empty">{t.loading}</div>}

      {!loading && templates.length === 0 && (
        <div className="pools-empty">{t.empty}</div>
      )}

      {!loading && templates.length > 0 && (
        <div className="pools-grid">
          {templates.map((tpl) => (
            <div key={tpl.id} className="pool-card" onClick={() => setSelectedId(tpl.id)}>
              <div className="pool-card-title">{tpl.name}</div>
              <div className="pool-card-row">
                <span>{t.players}</span>
                <strong>{entryCounts[tpl.id] || 0}</strong>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                <button className="pool-btn-cancel" style={{ flex: 1, padding: '6px', fontSize: 12 }}
                  onClick={(e) => { e.stopPropagation(); handleRename(tpl); }} disabled={busy}>
                  {t.rename}
                </button>
                <button className="pool-cancel-btn" style={{ flex: 1, padding: '6px', fontSize: 12 }}
                  onClick={(e) => { e.stopPropagation(); handleDelete(tpl); }} disabled={busy}>
                  {t.delete}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const TR = {
  en: {
    back: 'Back to pools',
    title: 'My Queue Templates',
    description: 'Build ranked lists of NHL players. Pick which template to use in each pool — on the clock, your timer running out will auto-pick from the top of your active template.',
    newTemplate: 'New Template',
    players: 'Players',
    rename: 'Rename',
    delete: 'Delete',
    loading: 'Loading…',
    empty: 'No templates yet. Create one to start building your queue.',
    promptName: 'Template name (e.g., "Playoff 2026", "Regular Season"):',
    promptRename: 'Rename template to:',
    confirmDelete: (n) => `Delete "${n}"? This cannot be undone.`,
    errCreate: 'Could not create template',
    errRename: 'Could not rename template',
    errDelete: 'Could not delete template',
  },
  fr: {
    back: 'Retour aux pools',
    title: 'Mes listes de repêchage',
    description: "Bâtissez des listes classées de joueurs NHL. Choisissez la liste active pour chaque pool — quand votre minuteur expire, le premier joueur disponible de votre liste est choisi automatiquement.",
    newTemplate: 'Nouvelle liste',
    players: 'Joueurs',
    rename: 'Renommer',
    delete: 'Supprimer',
    loading: 'Chargement…',
    empty: 'Aucune liste. Créez-en une pour commencer.',
    promptName: 'Nom de la liste (ex. "Séries 2026", "Saison régulière"):',
    promptRename: 'Renommer la liste en:',
    confirmDelete: (n) => `Supprimer "${n}"? Action irréversible.`,
    errCreate: 'Impossible de créer la liste',
    errRename: 'Impossible de renommer',
    errDelete: 'Impossible de supprimer',
  },
};
